import React, { useContext, useEffect, useState } from 'react'
import Button from '@Components/Buttons/Button'
import { StoresContext } from '@App/contexts'
import { observer } from 'mobx-react'
import { PublisherFormType } from '@App/enum/publisher-form-type'
import { LegacyPublisherSearch } from '@App/stores/search/models/publisher-search'
import PublisherSearchResultsTable from '@Components/WorkRegistrationSubs/PublisherFormSteps/PublisherSearchResultsTable'
import './index.scss'
import SearchWithAutoComplete from '@Components/Search/SearchWithAutoComplete'
import getPublisherNameAutoCompleteSuggestions from '@App/helpers/search/get-publisher-name-auto-complete-suggestions'
import { PublisherModelWithWorksCount } from '@Entities/work/publisher-model-with-works-count'
import { PublisherSearchStoreLike } from '@App/stores/search/publisher-search-store-like'

interface PublisherAddFormProps {
    itemWasAdded?: (publisherName: string) => boolean | undefined
    setFoundPublisherToDraft: (publisher: PublisherModelWithWorksCount) => void
    topClass: string
    title: string
    subtitle: string
    searchFilterPlaceholder: string
    suggestedTitle: string
    buttonText: string
    isAdministrator: boolean
    isUnrestricted?: boolean
    searchStore?: PublisherSearchStoreLike
}

const PublisherAddForm = (props: PublisherAddFormProps) => {
    const { publisherSearchStore, publisherStepStore } = useContext(StoresContext)
    const {
        itemWasAdded,
        setFoundPublisherToDraft,
        topClass,
        title,
        subtitle,
        searchFilterPlaceholder,
        suggestedTitle,
        buttonText,
        isAdministrator,
        isUnrestricted = false,
        searchStore = publisherSearchStore,
    } = props
    const [searchWasPerformed, setSearchWasPerformed] = useState(false)

    // TODO: Should set name/Ipi in the same function. Logic = if Ipi { set IPI} else {set Name}.
    // TODO: This should probably be handled in the publisherSearchStore.
    const setPublisherNameToSearch = (value: string) => {
        const publishersSearch: LegacyPublisherSearch = {
            ...searchStore.publisherSearch,
            publisherName: value,
            publisherIpi: null,
        }
        searchStore.setPublisherSearch(publishersSearch)
    }

    const onSubmitSingleSearch = async (value: string) => {
        const publishersSearch = {
            ...searchStore.publisherSearch,
            publisherName: value,
            publisherIpi: null,
        }
        searchStore.setPublisherSearch(publishersSearch)
        await searchStore.searchPublishers()
        setSearchWasPerformed(true)
    }

    const onClear = () => {
        searchStore.clearStore()
        setSearchWasPerformed(false)
    }

    useEffect(() => {
        if (!isUnrestricted) {
            onSubmitSingleSearch('').then()
        }
    }, [])

    const searchResult = searchStore.publisherSearchModel

    const publisherSearchModelHasData = searchResult && searchResult.count > 0
    const CreateNewPublisherSection = () => (
        <>
            <p className="cant-find-title">Can&apos;t find what you&apos;re looking for?</p>

            <div className="sidebar-wizard-button-container">
                <Button
                    mainColor
                    type="secondary"
                    className="sidebar-wizard-button"
                    onClick={() => publisherStepStore.setFormType(PublisherFormType.create)}
                >
                    {buttonText}
                </Button>
            </div>
        </>
    )

    return (
        <>
            <div className={topClass}>
                <h2>{title}</h2>
                <h5 className="medium subtitle">{subtitle}</h5>
                <SearchWithAutoComplete
                    id="publisher-search"
                    onChangeInput={setPublisherNameToSearch}
                    value={searchStore.publisherSearch?.publisherName}
                    placeholder={searchFilterPlaceholder}
                    buttonClick={onSubmitSingleSearch}
                    onClearInput={onClear}
                    autoCompleteFetcher={getPublisherNameAutoCompleteSuggestions}
                    autoCompleteField="fullName"
                />
                {searchStore.isSearching ? (
                    <p>Searching, please wait...</p>
                ) : (
                    publisherSearchModelHasData && (
                        <>
                            <PublisherSearchResultsTable
                                searchStore={searchStore}
                                suggestedTitle={suggestedTitle}
                                setFoundPublisherToDraft={setFoundPublisherToDraft}
                                itemWasAdded={itemWasAdded}
                                showMLCPublisherNumber
                                buttonText={isAdministrator ? 'Add administrator' : 'Add publisher'}
                            />

                            {!isUnrestricted && <CreateNewPublisherSection />}
                        </>
                    )
                )}

                {searchWasPerformed && !publisherSearchModelHasData && (
                    <>
                        <p className="suggested-title">Not found</p>

                        {!isUnrestricted && <CreateNewPublisherSection />}
                    </>
                )}
            </div>
        </>
    )
}

export default observer(PublisherAddForm)
